/**
 * 檢驗記錄新增/編輯表單 | Inspection Record Form
 * 用於新增或編輯來料檢驗/出貨檢驗記錄
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '@/components/UI/Modal';
import { InspectionItemDetails } from './InspectionItemDetails';
import type { InspectionRecord, InspectionRecordItem, InspectionItem } from '@/types/quality';
import { useOrderStore, useResourceStore, useQualityStore } from '@/store';
import { toast } from '@/components/UI/Toast';

interface InspectionRecordFormProps {
  record?: InspectionRecord; // 有值則為編輯模式
  type: '來料檢驗' | '出貨檢驗';
  isOpen: boolean;
  onClose: () => void;
  onSave: (record: Omit<InspectionRecord, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

// 檢驗狀態選項
const STATUS_OPTIONS = ['合格', '不合格', '條件合格', '待復檢'] as const;

// 不良類型選項
const DEFECT_TYPE_OPTIONS = [
  '尺寸不良',
  '外觀缺陷',
  '功能失效',
  '材料問題',
  '包裝損壞',
  '數量短缺',
  '標示錯誤',
  '其他',
];

interface FormData {
  type: '來料檢驗' | '出貨檢驗' | '製程檢驗';
  orderId: string;
  workOrderId: string;
  productId: string;
  batchNo: string;
  inspectionDate: string;
  inspectorId: string;
  inspectorName: string;
  sampleSize: number;
  qualifiedSize: number;
  status: InspectionRecord['status'];
  items: InspectionRecordItem[];
  hasDefect: boolean;
  defectTypes: string[];
  defectDescription: string;
  correctiveAction: string;
  reviewedBy: string;
  reviewedAt: string;
  reviewNotes: string;
  notes: string;
  defectiveSize: number;
  passRate: number;
}

interface FormErrors {
  orderId?: string;
  productId?: string;
  batchNo?: string;
  inspectionDate?: string;
  inspectorId?: string;
  sampleSize?: string;
  qualifiedSize?: string;
  status?: string;
  items?: string;
  defectDescription?: string;
}

export function InspectionRecordForm({
  record,
  type,
  isOpen,
  onClose,
  onSave,
}: InspectionRecordFormProps) {
  const isEditMode = !!record;

  const { orders } = useOrderStore();
  const { products, persons } = useResourceStore();
  const { inspectionItems } = useQualityStore();

  // 過濾出品檢員
  const inspectors = persons.filter((p) => p.roles.includes('品檢員'));

  // 表單狀態
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    type: type,
    orderId: '',
    workOrderId: '',
    productId: '',
    batchNo: '',
    inspectionDate: new Date().toISOString().slice(0, 10),
    inspectorId: '',
    inspectorName: '',
    sampleSize: 3,
    qualifiedSize: 3,
    status: '合格',
    items: [],
    hasDefect: false,
    defectTypes: [],
    defectDescription: '',
    correctiveAction: '',
    reviewedBy: '',
    reviewedAt: '',
    reviewNotes: '',
    notes: '',
    defectiveSize: 0,
    passRate: 100,
  });

  // 使用 useMemo 計算衍生值（不合格數量、合格率）
  const defectiveSize = useMemo(
    () => Math.max(0, formData.sampleSize - formData.qualifiedSize),
    [formData.sampleSize, formData.qualifiedSize]
  );

  const passRate = useMemo(
    () => (formData.sampleSize > 0 ? (formData.qualifiedSize / formData.sampleSize) * 100 : 0),
    [formData.sampleSize, formData.qualifiedSize]
  );

  // 驗證錯誤
  const [errors, setErrors] = useState<FormErrors>({});
  // 追蹤已被觸碰的欄位
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // 當 record 改變時，更新表單資料
  useEffect(() => {
    if (record) {
      setFormData({
        type: record.type,
        orderId: record.orderId || '',
        workOrderId: record.workOrderId || '',
        productId: record.productId || '',
        batchNo: record.batchNo,
        inspectionDate: record.inspectionDate.slice(0, 10),
        inspectorId: record.inspectorId,
        inspectorName: record.inspectorName,
        sampleSize: record.sampleSize,
        qualifiedSize: record.qualifiedSize,
  
  
        status: record.status,
        items: record.items,
        hasDefect: record.hasDefect,
        defectTypes: record.defectTypes || [],
        defectDescription: record.defectDescription || '',
        correctiveAction: record.correctiveAction || '',
        reviewedBy: record.reviewedBy || '',
        reviewedAt: record.reviewedAt?.slice(0, 10) || '',
        reviewNotes: record.reviewNotes || '',
        notes: record.notes || '',
        defectiveSize: record.defectiveSize,
        passRate: record.passRate,
  
  
      });
    } else {
      // 新增模式的預設值
      setFormData({
        type: type,
        orderId: '',
        workOrderId: '',
        productId: '',
        batchNo: '',
        inspectionDate: new Date().toISOString().slice(0, 10),
        inspectorId: '',
        inspectorName: '',
        sampleSize: 3,
        qualifiedSize: 3,


        status: '合格',
        items: [],
        hasDefect: false,
        defectTypes: [],
        defectDescription: '',
        correctiveAction: '',
        reviewedBy: '',
        reviewedAt: '',
        reviewNotes: '',
        notes: '',
        defectiveSize: 0,
        passRate: 100,


      });
    }
  }, [record, isOpen, type]);

  // 當 productId 變更時，自動載入該產品的檢核項目
  useEffect(() => {
    if (formData.productId && !isEditMode) {
      const productInspectionItems = inspectionItems.filter(
        (item) => !item.productId || item.productId === formData.productId
      );

      if (productInspectionItems.length > 0) {
        const newItems: InspectionRecordItem[] = productInspectionItems.map((item) => ({
          inspectionItemId: item.id,
          itemName: item.name,
          standard: item.standard,
          measuredValue: '',
          isQualified: true,
          notes: '',
    defectiveSize: 0,
    passRate: 100,


        }));

        setFormData((prev) => ({ ...prev, items: newItems }));
      }
    }
  }, [formData.productId, inspectionItems, isEditMode]);

  // 即時計算合格率和不合格數量
  useEffect(() => {
    const defectiveSize = formData.sampleSize - formData.qualifiedSize;
    const passRate = formData.sampleSize > 0 ? (formData.qualifiedSize / formData.sampleSize) * 100 : 0;

    setFormData((prev) => ({
      ...prev,
      defectiveSize: Math.max(0, defectiveSize),
      passRate,
    }));
  }, [formData.sampleSize, formData.qualifiedSize]);

  // 即時驗證單一欄位
  const validateField = useCallback((field: keyof FormData, value: any): string => {
    switch (field) {
      case 'batchNo':
        if (!value.trim()) return '批號不能為空';
        return '';
      case 'inspectionDate':
        if (!value) return '檢驗日期不能為空';
        return '';
      case 'inspectorId':
        if (!value) return '請選擇檢驗員';
        return '';
      case 'sampleSize':
        if (value === '' || value === null || value === undefined) return '抽樣數量不能為空';
        if (value < 1) return '抽樣數量必須大於 0';
        return '';
      case 'qualifiedSize':
        if (value === '' || value === null || value === undefined) return '合格數量不能為空';
        if (value < 0) return '合格數量不能小於 0';
        if (value > formData.sampleSize) return '合格數量不能大於抽樣數量';
        return '';
      case 'status':
        if (!value) return '請選擇檢驗狀態';
        if (value === '不合格' && formData.hasDefect && !formData.defectDescription.trim()) {
          return '不合格時必須填寫不良描述';
        }
        return '';
      case 'items':
        if (formData.items.length === 0) return '至少需要一個檢核項目';
        return '';
      default:
        return '';
    }
  }, [formData.items.length, formData.sampleSize, formData.hasDefect, formData.defectDescription]);

  // 處理欄位變更
  const handleFieldChange = useCallback((field: keyof FormData, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // 處理關聯欄位更新
      if (field === 'inspectorId') {
        const inspector = inspectors.find((i) => i.id === value);
        newData.inspectorName = inspector?.name || '';
      }

      if (field === 'productId') {
        const product = products.find((p) => p.id === value);
        if (product && !prev.batchNo) {
          newData.batchNo = `${type === '來料檢驗' ? 'BATCH' : 'SHIP'}-${Date.now().toString().slice(-4)}`;
        }
      }

      // 數值欄位驗證和邊界處理
      if (field === 'sampleSize') {
        const numValue = typeof value === 'number' ? value : value === '' ? 0 : Number(value);
        if (!isNaN(numValue) && numValue >= 1) {
          newData.sampleSize = numValue;
          // 如果新的抽樣數量小於當前合格數量，自動調整合格數量
          if (numValue < newData.qualifiedSize) {
            newData.qualifiedSize = numValue;
          }
        }
      }

      if (field === 'qualifiedSize') {
        const numValue = typeof value === 'number' ? value : value === '' ? 0 : Number(value);
        if (!isNaN(numValue) && numValue >= 0) {
          // 限制合格數量不超過抽樣數量
          newData.qualifiedSize = Math.min(numValue, newData.sampleSize);
        }
      }

      return newData;
    });

    // 如果該欄位已被觸碰，進行即時驗證
    if (touched.has(field)) {
      const error = validateField(field, formData[field]);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[field as keyof FormErrors] = error;
        } else {
          delete newErrors[field as keyof FormErrors];
        }
        return newErrors;
      });
    } else {
      // 如果未被觸碰，只清除錯誤
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FormErrors];
        return newErrors;
      });
    }
  }, [inspectors, products, type, touched, validateField, formData]);

  // 處理欄位失焦
  const handleFieldBlur = useCallback((field: keyof FormData) => {
    setTouched((prev) => new Set([...prev, field]));
    const error = validateField(field, formData[field]);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field as keyof FormErrors] = error;
      } else {
        delete newErrors[field as keyof FormErrors];
      }
      return newErrors;
    });
  }, [formData, validateField]);

  // 驗證表單
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const batchNoError = validateField('batchNo', formData.batchNo);
    if (batchNoError) newErrors.batchNo = batchNoError;

    const inspectionDateError = validateField('inspectionDate', formData.inspectionDate);
    if (inspectionDateError) newErrors.inspectionDate = inspectionDateError;

    const inspectorIdError = validateField('inspectorId', formData.inspectorId);
    if (inspectorIdError) newErrors.inspectorId = inspectorIdError;

    const sampleSizeError = validateField('sampleSize', formData.sampleSize);
    if (sampleSizeError) newErrors.sampleSize = sampleSizeError;

    const qualifiedSizeError = validateField('qualifiedSize', formData.qualifiedSize);
    if (qualifiedSizeError) newErrors.qualifiedSize = qualifiedSizeError;

    const statusError = validateField('status', formData.status);
    if (statusError) newErrors.status = statusError;

    const itemsError = validateField('items', formData.items);
    if (itemsError) newErrors.items = itemsError;

    if (formData.status === '不合格' && formData.hasDefect && !formData.defectDescription.trim()) {
      newErrors.defectDescription = '不合格時必須填寫不良描述';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField]);

  // 提交表單
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (isSubmitting) return;

      if (!validateForm()) {
        toast.error('表單驗證失敗', '請檢查表單內容是否正確');
        return;
      }

      setIsSubmitting(true);

      try {
        onSave({
          type: formData.type,
          orderId: formData.orderId || undefined,
          workOrderId: formData.workOrderId || undefined,
          productId: formData.productId || undefined,
          batchNo: formData.batchNo,
          inspectionDate: new Date(formData.inspectionDate).toISOString(),
          inspectorId: formData.inspectorId,
          inspectorName: formData.inspectorName,
          sampleSize: formData.sampleSize,
          qualifiedSize: formData.qualifiedSize,
          defectiveSize: formData.defectiveSize,
          passRate: formData.passRate,
          status: formData.status,
          items: formData.items,
          hasDefect: formData.hasDefect,
          defectTypes: formData.hasDefect ? formData.defectTypes : undefined,
          defectDescription: formData.hasDefect ? formData.defectDescription : undefined,
          correctiveAction: formData.correctiveAction || undefined,
          reviewedBy: formData.reviewedBy || undefined,
          reviewedAt: formData.reviewedAt ? new Date(formData.reviewedAt).toISOString() : undefined,
          reviewNotes: formData.reviewNotes || undefined,
          notes: formData.notes || undefined,
        });

        toast.success(
          isEditMode ? '檢驗記錄已更新' : '檢驗記錄已建立',
          `${type} (${formData.batchNo}) 已成功${isEditMode ? '更新' : '建立'}`,
          3000
        );

        onClose();
      } catch (error) {
        console.error('保存檢驗記錄時發生錯誤:', error);
        toast.error(
          '保存失敗',
          error instanceof Error ? error.message : '發生未預期的錯誤，請稍後再試',
          5000
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, isEditMode, isSubmitting, type, validateForm, onSave, onClose]
  );

  // 處理不良類型切換
  const handleDefectTypeToggle = useCallback((defectType: string) => {
    const newTypes = formData.defectTypes.includes(defectType)
      ? formData.defectTypes.filter((t) => t !== defectType)
      : [...formData.defectTypes, defectType];

    setFormData((prev) => ({ ...prev, defectTypes: newTypes }));
  }, [formData.defectTypes]);

  // 輸入框樣式（含錯誤狀態）
  const getInputClass = (fieldName: string) => {
    const baseClass =
      'w-full px-4 py-2.5 bg-white border-2 rounded-xl text-sm font-medium transition-all focus:ring-2 focus:ring-offset-0 focus:outline-none';
    const errorClass = errors[fieldName as keyof FormErrors]
      ? 'border-red-300 focus:ring-red-400 focus:border-red-400'
      : `border-slate-200 focus:ring-${type === '來料檢驗' ? 'blue' : 'green'}-400 focus:border-${type === '來料檢驗' ? 'blue' : 'green'}-300`;
    return `${baseClass} ${errorClass}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? `✏️ 編輯${type}` : `➕ 新增${type}`}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* === 基本資料區段 === */}
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white text-xs">
              1
            </span>
            基本資料
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 關聯訂單 */}
            <div>
              <label htmlFor="orderId" className="block text-sm font-medium text-slate-700 mb-1">
                關聯訂單
              </label>
              <select
                id="orderId"
                value={formData.orderId}
                onChange={(e) => handleFieldChange('orderId', e.target.value)}
                className={getInputClass('orderId')}
                aria-describedby="orderId-hint"
              >
                <option value="">請選擇訂單（選填）</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.id} - {order.productName} ({order.customer})
                  </option>
                ))}
              </select>
              <p id="orderId-hint" className="mt-1 text-xs text-slate-500">
                選填，關聯的生產訂單
              </p>
            </div>

            {/* 關聯產品 */}
            <div>
              <label htmlFor="productId" className="block text-sm font-medium text-slate-700 mb-1">
                關聯產品
              </label>
              <select
                id="productId"
                value={formData.productId}
                onChange={(e) => handleFieldChange('productId', e.target.value)}
                className={getInputClass('productId')}
                aria-describedby="productId-hint"
              >
                <option value="">請選擇產品（選填）</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.id} - {product.name}
                  </option>
                ))}
              </select>
              <p id="productId-hint" className="mt-1 text-xs text-slate-500">
                選填，將自動載入該產品的檢核項目
              </p>
            </div>

            {/* 批號 */}
            <div>
              <label htmlFor="batchNo" className="block text-sm font-medium text-slate-700 mb-1">
                批號 <span className="text-red-500">*</span>
              </label>
              <input
                id="batchNo"
                type="text"
                value={formData.batchNo}
                onChange={(e) => handleFieldChange('batchNo', e.target.value)}
                onBlur={() => handleFieldBlur('batchNo')}
                className={getInputClass('batchNo')}
                placeholder="BATCH-2026-001"
                required
                aria-required="true"
                aria-invalid={errors.batchNo && touched.has('batchNo') ? 'true' : 'false'}
                aria-describedby={errors.batchNo && touched.has('batchNo') ? 'batchNo-error' : undefined}
              />
              {errors.batchNo && touched.has('batchNo') && (
                <p id="batchNo-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.batchNo}</span>
                </p>
              )}
            </div>

            {/* 檢驗日期 */}
            <div>
              <label htmlFor="inspectionDate" className="block text-sm font-medium text-slate-700 mb-1">
                檢驗日期 <span className="text-red-500">*</span>
              </label>
              <input
                id="inspectionDate"
                type="date"
                value={formData.inspectionDate}
                onChange={(e) => handleFieldChange('inspectionDate', e.target.value)}
                onBlur={() => handleFieldBlur('inspectionDate')}
                className={getInputClass('inspectionDate')}
                required
                aria-required="true"
                aria-invalid={errors.inspectionDate && touched.has('inspectionDate') ? 'true' : 'false'}
                aria-describedby={
                  errors.inspectionDate && touched.has('inspectionDate') ? 'inspectionDate-error' : undefined
                }
              />
              {errors.inspectionDate && touched.has('inspectionDate') && (
                <p id="inspectionDate-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.inspectionDate}</span>
                </p>
              )}
            </div>

            {/* 檢驗員 */}
            <div>
              <label htmlFor="inspectorId" className="block text-sm font-medium text-slate-700 mb-1">
                檢驗員 <span className="text-red-500">*</span>
              </label>
              <select
                id="inspectorId"
                value={formData.inspectorId}
                onChange={(e) => handleFieldChange('inspectorId', e.target.value)}
                onBlur={() => handleFieldBlur('inspectorId')}
                className={getInputClass('inspectorId')}
                required
                aria-required="true"
                aria-invalid={errors.inspectorId && touched.has('inspectorId') ? 'true' : 'false'}
                aria-describedby={
                  errors.inspectorId && touched.has('inspectorId') ? 'inspectorId-error' : undefined
                }
              >
                <option value="">請選擇檢驗員</option>
                {inspectors.map((inspector) => (
                  <option key={inspector.id} value={inspector.id}>
                    {inspector.name} ({inspector.id})
                  </option>
                ))}
              </select>
              {errors.inspectorId && touched.has('inspectorId') && (
                <p id="inspectorId-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.inspectorId}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* === 檢驗結果區段 === */}
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center text-white text-xs">
              2
            </span>
            檢驗結果
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="sampleSize" className="block text-sm font-medium text-slate-700 mb-1">
                抽樣數量 <span className="text-red-500">*</span>
              </label>
              <input
                id="sampleSize"
                type="number"
                min="1"
                value={formData.sampleSize}
                onChange={(e) => handleFieldChange('sampleSize', Number(e.target.value))}
                onBlur={() => handleFieldBlur('sampleSize')}
                className={getInputClass('sampleSize')}
                required
                aria-required="true"
                aria-invalid={errors.sampleSize && touched.has('sampleSize') ? 'true' : 'false'}
                aria-describedby={
                  errors.sampleSize && touched.has('sampleSize') ? 'sampleSize-error' : undefined
                }
              />
              {errors.sampleSize && touched.has('sampleSize') && (
                <p id="sampleSize-error" className="mt-1.5 text-sm text-red-600" role="alert">
                  ⚠️ {errors.sampleSize}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="qualifiedSize" className="block text-sm font-medium text-slate-700 mb-1">
                合格數量 <span className="text-red-500">*</span>
              </label>
              <input
                id="qualifiedSize"
                type="number"
                min="0"
                max={formData.sampleSize}
                value={formData.qualifiedSize}
                onChange={(e) => handleFieldChange('qualifiedSize', Number(e.target.value))}
                onBlur={() => handleFieldBlur('qualifiedSize')}
                className={getInputClass('qualifiedSize')}
                required
                aria-required="true"
                aria-invalid={errors.qualifiedSize && touched.has('qualifiedSize') ? 'true' : 'false'}
                aria-describedby={
                  errors.qualifiedSize && touched.has('qualifiedSize') ? 'qualifiedSize-error' : undefined
                }
              />
              {errors.qualifiedSize && touched.has('qualifiedSize') && (
                <p id="qualifiedSize-error" className="mt-1.5 text-sm text-red-600" role="alert">
                  ⚠️ {errors.qualifiedSize}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">不合格數量</label>
              <div className="w-full px-4 py-2.5 bg-slate-100 border-2 border-slate-200 rounded-xl text-sm font-bold text-slate-700">
                {formData.defectiveSize}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">合格率</label>
              <div className="w-full px-4 py-2.5 bg-slate-100 border-2 border-slate-200 rounded-xl text-sm font-bold text-slate-700">
                {formData.passRate.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* 檢驗狀態 */}
          <div className="mt-4">
            <label htmlFor="status" className="block text-sm font-medium text-slate-700 mb-1">
              檢驗狀態 <span className="text-red-500">*</span>
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) =>
                handleFieldChange('status', e.target.value as InspectionRecord['status'])
              }
              onBlur={() => handleFieldBlur('status')}
              className={getInputClass('status')}
              required
              aria-required="true"
              aria-invalid={errors.status && touched.has('status') ? 'true' : 'false'}
              aria-describedby={errors.status && touched.has('status') ? 'status-error' : undefined}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.status && touched.has('status') && (
              <p id="status-error" className="mt-1.5 text-sm text-red-600" role="alert">
                ⚠️ {errors.status}
              </p>
            )}
          </div>
        </div>

        {/* === 檢核項目明細區段 === */}
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-xs">
              3
            </span>
            檢核項目明細
          </h3>
          <InspectionItemDetails
            items={formData.items}
            inspectionItems={inspectionItems}
            onChange={(items) => setFormData((prev) => ({ ...prev, items }))}
          />
          {errors.items && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              ⚠️ {errors.items}
            </p>
          )}
        </div>

        {/* === 不良處理區段 === */}
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-xs">
              4
            </span>
            不良處理
          </h3>

          {/* 是否有不良 */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hasDefect}
                onChange={(e) => handleFieldChange('hasDefect', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-slate-700">是否有不良</span>
            </label>
          </div>

          {formData.hasDefect && (
            <div className="space-y-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              {/* 不良類型 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">不良類型</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {DEFECT_TYPE_OPTIONS.map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.defectTypes.includes(type)}
                        onChange={() => handleDefectTypeToggle(type)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-slate-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 不良描述 */}
              <div>
                <label htmlFor="defectDescription" className="block text-sm font-medium text-slate-700 mb-1">
                  不良描述 {formData.status === '不合格' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  id="defectDescription"
                  value={formData.defectDescription}
                  onChange={(e) => handleFieldChange('defectDescription', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white border-2 border-red-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-red-400 focus:border-red-400 focus:outline-none resize-none"
                  placeholder="詳細描述不良情形..."
                  aria-invalid={errors.defectDescription ? 'true' : 'false'}
                  aria-describedby={errors.defectDescription ? 'defectDescription-error' : undefined}
                />
                {errors.defectDescription && (
                  <p id="defectDescription-error" className="mt-1.5 text-sm text-red-600" role="alert">
                    ⚠️ {errors.defectDescription}
                  </p>
                )}
              </div>

              {/* 矯正措施 */}
              <div>
                <label htmlFor="correctiveAction" className="block text-sm font-medium text-slate-700 mb-1">
                  矯正措施
                </label>
                <textarea
                  id="correctiveAction"
                  value={formData.correctiveAction}
                  onChange={(e) => handleFieldChange('correctiveAction', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none resize-none"
                  placeholder="描述採取的矯正措施..."
                />
              </div>
            </div>
          )}
        </div>

        {/* === 審核區段 === */}
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-xs">
              5
            </span>
            審核
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 審核者 */}
            <div>
              <label htmlFor="reviewedBy" className="block text-sm font-medium text-slate-700 mb-1">
                審核者
              </label>
              <select
                id="reviewedBy"
                value={formData.reviewedBy}
                onChange={(e) => handleFieldChange('reviewedBy', e.target.value)}
                className={getInputClass('reviewedBy')}
                aria-describedby="reviewedBy-hint"
              >
                <option value="">請選擇審核者（選填）</option>
                {inspectors.map((inspector) => (
                  <option key={inspector.id} value={inspector.id}>
                    {inspector.name} ({inspector.id})
                  </option>
                ))}
              </select>
              <p id="reviewedBy-hint" className="mt-1 text-xs text-slate-500">
                選填，選擇後此記錄需審核通過
              </p>
            </div>

            {/* 審核時間 */}
            <div>
              <label htmlFor="reviewedAt" className="block text-sm font-medium text-slate-700 mb-1">
                審核時間
              </label>
              <input
                id="reviewedAt"
                type="date"
                value={formData.reviewedAt}
                onChange={(e) => handleFieldChange('reviewedAt', e.target.value)}
                className={getInputClass('reviewedAt')}
                aria-describedby="reviewedAt-hint"
              />
              <p id="reviewedAt-hint" className="mt-1 text-xs text-slate-500">
                選填，預設為當前時間
              </p>
            </div>
          </div>

          {/* 審核備註 */}
          <div className="mt-4">
            <label htmlFor="reviewNotes" className="block text-sm font-medium text-slate-700 mb-1">
              審核備註
            </label>
            <textarea
              id="reviewNotes"
              value={formData.reviewNotes}
              onChange={(e) => handleFieldChange('reviewNotes', e.target.value)}
              rows={2}
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none resize-none"
              placeholder="選填，審核意見或備註..."
            />
          </div>
        </div>

        {/* === 備註區段 === */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-1">
            備註
          </label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            rows={2}
            className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none resize-none"
            placeholder="選填，其他備註事項..."
          />
        </div>

        {/* === 按鈕區段 === */}
        <div className="flex justify-end gap-3 pt-6 border-t-2 border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-6 py-2.5 text-white rounded-xl transition-all text-sm font-semibold shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg flex items-center gap-2 ${
              type === '來料檢驗'
                ? 'bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 shadow-blue-500/30 hover:shadow-blue-500/40'
                : 'bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 shadow-green-500/30 hover:shadow-green-500/40'
            }`}
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <span>處理中...</span>
              </>
            ) : (
              <>{isEditMode ? '💾 儲存變更' : '➕ 新增記錄'}</>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
